import {
  LoadingVisual,
  type LoadingVisualVariant,
} from '@/components/ui/loading-visual';
import type { BrandingSettings, UISettings } from '@/contexts/SiteSettingsContext';
import { useOptionalSiteSettings } from '@/contexts/SiteSettingsContext';
import { useOptionalPublicBranding } from '@/contexts/PublicBrandingContext';
import { resolveBrandAwareLoading } from '@/lib/branding/resolveBrandAwareLoading';
import { cn } from '@/lib/utils';

type ConfiguredLoadingScope = 'fullscreen' | 'content' | 'inline' | 'boot';

export interface ConfiguredLoadingVisualProps {
  scope: ConfiguredLoadingScope;
  className?: string;
  /** @deprecated */
  subtitle?: string;
  logoAlt?: string;
  variant?: LoadingVisualVariant;
  spinnerLabel?: string;
  animationsEnabled?: boolean;
  showStatusLabel?: boolean;
  /** runtime = no card; preview = config UI context */
  presentation?: 'runtime' | 'preview';
  /** Admin draft branding merged into loading block (preview). */
  draftBranding?: Partial<BrandingSettings> | null;
  /** Admin draft UI merged into loading block (preview). */
  draftUi?: Partial<UISettings> | null;
}

export function ConfiguredLoadingVisual({
  scope,
  className,
  subtitle: _subtitle,
  logoAlt,
  variant,
  spinnerLabel,
  animationsEnabled,
  showStatusLabel = false,
  presentation = 'runtime',
  draftBranding,
  draftUi,
}: ConfiguredLoadingVisualProps) {
  const siteSettings = useOptionalSiteSettings();
  const publicBrandingCtx = useOptionalPublicBranding();

  const resolved = resolveBrandAwareLoading({
    scope,
    siteBranding: siteSettings?.settings.branding,
    siteUi: siteSettings?.settings.ui,
    publicBranding: publicBrandingCtx?.branding ?? null,
    draftBranding,
    draftUi,
    useDraft: !!(draftBranding || draftUi),
    animationsOverride: animationsEnabled,
  });

  const resolvedVariant: LoadingVisualVariant =
    variant ?? (scope === 'fullscreen' || scope === 'boot' ? 'full' : 'compact');

  const graphicSize =
    scope === 'inline' ? 'inline' as const : scope === 'fullscreen' || scope === 'boot' ? 'fullscreen' as const : 'content' as const;

  const fillClassName = scope === 'inline' ? 'bg-current' : 'bg-primary';

  const statusLabel = spinnerLabel?.trim() || 'Đang tải';

  const markerProps = {
    'data-configured-loading': 'true',
    'data-loading-animation-type': resolved.animationType,
    'data-loading-duration-ms': String(resolved.durationMs),
    'data-loading-scope': scope,
    'data-loading-config-source': resolved.configSource,
    'data-loading-brand-asset': resolved.brandAssetSource,
    'data-loading-title-present': resolved.showTitle && !!resolved.title ? 'true' : 'false',
    'data-loading-presentation': presentation,
    'data-loading-brand-visible':
      resolved.showBrandAsset && !!resolved.brandAssetUrl ? 'true' : 'false',
    'data-loading-title-visible':
      resolved.showTitle && !!resolved.title ? 'true' : 'false',
    'data-loading-asset-type': resolved.brandAssetSource,
    'data-loading-context': scope,
  } as const;

  const visual = (
    <LoadingVisual
      animationType={resolved.animationType}
      durationMs={resolved.durationMs}
      variant={resolvedVariant}
      animationsEnabled={resolved.animationsEnabled}
      graphicSize={graphicSize}
      fillClassName={fillClassName}
      showStatusLabel={showStatusLabel}
      statusLabel={statusLabel}
      appName={resolved.title ?? undefined}
      showTitle={resolved.showTitle}
      showBrandAsset={resolved.showBrandAsset}
      brandAssetUrl={resolved.brandAssetUrl}
      logoAlt={logoAlt}
      glassEnabled={false}
      scope={scope}
      presentation={presentation}
      className={scope === 'inline' ? undefined : 'text-muted-foreground'}
    />
  );

  if (scope === 'inline') {
    return (
      <span {...markerProps} className={cn('inline-flex items-center', className)} aria-hidden="true">
        {visual}
      </span>
    );
  }

  return (
    <div {...markerProps} className={className}>
      {visual}
    </div>
  );
}
